import { PdfPage } from 'components/PdfExportConfigDialog/hooks/types';
import { Logger } from 'utilities';
import { capturePage } from './captureIframe';
import { PageResult } from './types';

// Capture pages one at a time by default. Parallel captures share the user's
// request quota (UserRateThrottle is 500/min across all in-flight iframes) and
// the host machine's CPU, and under that pressure a page can exhaust its stall
// budget and be dropped from the PDF — an export that silently loses a page is
// far worse than a slower one. The dialog slider still exposes up to
// MAX_CONCURRENCY for anyone who wants to trade that risk for throughput.
export const DEFAULT_CONCURRENCY = 1;
export const MIN_CONCURRENCY = 1;
export const MAX_CONCURRENCY = 8;

/**
 * Default parallel-capture concurrency. Deliberately not derived from machine
 * capability: the failure it guards against is request-quota and server load,
 * which a fast client does nothing to relieve.
 */
export const getDefaultConcurrency = (): number => DEFAULT_CONCURRENCY;

// Number of times the orchestrator will retry a single page if its iframe
// stalls (waitForReadiness exits with reason='stalled' because the React app
// inside the iframe is hung). Most stalls recover on a fresh iframe, where
// the second attempt gets new connections, a fresh React Query cache, etc.
const MAX_STALL_RETRIES = 1;

interface RunOrchestratorArgs {
  pages: PdfPage[];
  signal: AbortSignal;
  onPageUpdate: (result: PageResult) => void;
  concurrency?: number;
}

/**
 * Drives the capture pipeline with a configurable concurrency.
 * Order of returned results matches input order, regardless of completion order.
 */
export const runCapturePipeline = async ({
  pages,
  signal,
  onPageUpdate,
  concurrency,
}: RunOrchestratorArgs): Promise<PageResult[]> => {
  const results: PageResult[] = pages.map((p, index) => ({
    index,
    title: p.title,
    exportTarget: p.exportTarget,
    url: p.url,
    status: 'pending',
  }));

  let nextIndex = 0;

  const takeNext = (): number | null => {
    if (signal.aborted) {
      return null;
    }
    if (nextIndex >= pages.length) {
      return null;
    }
    const idx = nextIndex;
    nextIndex += 1;
    return idx;
  };

  const emit = (idx: number) => {
    onPageUpdate({ ...results[idx] });
  };

  const worker = async () => {
    while (!signal.aborted) {
      const idx = takeNext();
      if (idx === null) {
        return;
      }
      const page = pages[idx];

      results[idx] = { ...results[idx], status: 'loading', startedAt: performance.now() };
      emit(idx);

      // eslint-disable-next-line no-await-in-loop
      let verdict = await capturePage({ url: page.url, exportTarget: page.exportTarget, signal });
      // Retry once on a fresh iframe if the React app inside hung (stall
      // recovery). The teardown of the previous iframe in capturePage frees
      // the connections/cache that may have been the cause.
      let retries = 0;
      while (
        retries < MAX_STALL_RETRIES &&
        verdict.kind === 'failed' &&
        verdict.reason === 'stalled' &&
        !signal.aborted
      ) {
        retries += 1;
        Logger.warn(`Retrying stalled capture for ${page.exportTarget} (attempt ${retries + 1})`);
        // eslint-disable-next-line no-await-in-loop
        verdict = await capturePage({ url: page.url, exportTarget: page.exportTarget, signal });
      }

      if (signal.aborted || verdict.kind === 'canceled') {
        results[idx] = { ...results[idx], status: 'canceled', finishedAt: performance.now() };
        emit(idx);
        return;
      }

      if (verdict.kind === 'ready') {
        results[idx] = {
          ...results[idx],
          status: 'done',
          finishedAt: performance.now(),
          image: verdict.image,
          footer: verdict.footer,
        };
        emit(idx);
        // Release this worker's reference to the captured Blob now that the
        // consumer has its own copy via emit's spread. Holding it here would
        // keep every captured Blob alive in `results` until the entire
        // pipeline resolves, defeating the streaming PDF assembly downstream.
        results[idx] = { ...results[idx], image: null };
      } else if (verdict.kind === 'missing') {
        results[idx] = {
          ...results[idx],
          status: 'skipped',
          finishedAt: performance.now(),
          reason: 'target not present',
          image: null,
        };
        emit(idx);
      } else {
        results[idx] = {
          ...results[idx],
          status: 'failed',
          finishedAt: performance.now(),
          reason: verdict.reason,
          image: null,
        };
        emit(idx);
      }
    }
  };

  const resolvedConcurrency = Number.isFinite(concurrency) ? Number(concurrency) : getDefaultConcurrency();
  const clampedConcurrency = Math.max(
    MIN_CONCURRENCY,
    Math.min(MAX_CONCURRENCY, Math.floor(resolvedConcurrency) || DEFAULT_CONCURRENCY)
  );

  const workers: Promise<void>[] = [];
  const workerCount = Math.min(clampedConcurrency, pages.length);
  for (let i = 0; i < workerCount; i++) {
    workers.push(worker());
  }
  await Promise.all(workers);

  return results;
};
